/**
 * AccessCodeField — the 4-digit code, in two modes.
 *
 * GAL-11.
 *   draft   — creation flow: the code is held locally and written at
 *             Create. `value` / `onChange` only.
 *   saved   — editor: the row has (or hasn't) a hashed code. Shows
 *             On/Off, lets the studio change or remove it via
 *             `onSave(code)` / `onRemove()`. The code can never be
 *             read back, only replaced.
 */
import { useState } from 'react';
import { Check, KeyRound } from 'lucide-react';
import { OrbitIcon } from '@/components/icons/OrbitIcon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

const DIGITS = /^\d{4}$/;

export function AccessCodeField({
  mode,
  value = '',
  onChange,
  isSet = false,
  onSave,
  onRemove,
  disabled = false,
  hint,
  className,
}: {
  mode: 'draft' | 'saved';
  value?: string;
  onChange?: (v: string) => void;
  isSet?: boolean;
  onSave?: (code: string) => Promise<void> | void;
  onRemove?: () => Promise<void> | void;
  disabled?: boolean;
  /** Overrides the default helper copy. */
  hint?: string;
  className?: string;
}) {
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(false);
  const clean = (v: string) => v.replace(/\D/g, '').slice(0, 4);

  const header = (
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="flex items-center gap-1.5 text-sm font-medium text-foreground">
          <OrbitIcon name="key.fill" fallback={KeyRound} className="h-3.5 w-3.5 text-emerald-400" />
          Access code
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {hint ??
            (mode === 'saved' && isSet
              ? 'Clients enter a 4-digit code before they see anything. Stored hashed — you can change it, not read it.'
              : 'Optional. Clients enter a 4-digit code before they see anything.')}
        </p>
      </div>
      {mode === 'saved' && isSet && !editing && (
        <span className="shrink-0 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-300">
          On
        </span>
      )}
    </div>
  );

  if (mode === 'draft') {
    return (
      <div className={cn('space-y-3', className)}>
        {header}
        <Input
          value={value}
          onChange={(e) => onChange?.(clean(e.target.value))}
          placeholder="4 digits · leave blank for none"
          inputMode="numeric"
          disabled={disabled}
          className="h-9 w-56 font-mono tracking-[0.3em] placeholder:tracking-normal placeholder:font-sans"
        />
      </div>
    );
  }

  const valid = DIGITS.test(draft);
  const save = async () => {
    if (!valid) return;
    await onSave?.(draft);
    setDraft('');
    setEditing(false);
  };

  return (
    <div className={cn('space-y-3', className)}>
      {header}
      {(!isSet || editing) && (
        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={draft}
            onChange={(e) => setDraft(clean(e.target.value))}
            onKeyDown={(e) => { if (e.key === 'Enter') void save(); }}
            placeholder="4 digits"
            inputMode="numeric"
            disabled={disabled}
            className="h-9 w-32 font-mono tracking-[0.3em]"
          />
          <Button size="sm" className="rounded-full" onClick={() => void save()} disabled={disabled || !valid}>
            <OrbitIcon name="checkmark" fallback={Check} className="mr-1 h-3.5 w-3.5" />
            {isSet ? 'Change code' : 'Add code'}
          </Button>
          {editing && (
            <Button variant="ghost" size="sm" className="rounded-full" onClick={() => { setEditing(false); setDraft(''); }}>
              Cancel
            </Button>
          )}
        </div>
      )}
      {isSet && !editing && (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => setEditing(true)} disabled={disabled}>
            Change code
          </Button>
          <Button variant="ghost" size="sm" className="rounded-full text-muted-foreground" onClick={() => void onRemove?.()} disabled={disabled}>
            Remove
          </Button>
        </div>
      )}
    </div>
  );
}

export default AccessCodeField;
